import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Channel } from "@/mocks/streams";
import { formatViewers } from "@/lib/format";

interface HeroCarouselProps {
  items: Channel[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const go = (index: number) => setActive((index + items.length) % items.length);
  const current = items[active];

  return (
    <div
      className="relative h-[360px] w-full overflow-hidden rounded-xl bg-background-200 md:h-[460px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={item.cover}
            alt={`${item.title} - ${item.hostName} - 有因直播`}
            title={`${item.title} ${item.hostName}`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/55 to-background-50/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/80 via-transparent to-transparent" />
        </div>
      ))}

      <div className="absolute left-6 right-6 top-6 flex items-center justify-between">
        <span className="flex items-center gap-2 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-medium text-background-50">
          <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse" />
          正在直播中
        </span>
        <span className="hidden rounded-full bg-background-50/70 px-3 py-1 text-xs text-foreground-600 backdrop-blur sm:block">
          焦点直播推荐
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-3">
            <div className="w-10 h-10 overflow-hidden rounded-full border border-background-300">
              <img src={current.hostAvatar} alt={`主播 ${current.hostName}`} className="h-full w-full object-cover" />
            </div>
            <span className="text-sm text-foreground-700">{current.hostName}</span>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line text-sm"></i>
              </span>
              {formatViewers(current.viewers)} 人观看
            </span>
          </div>
          <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">{current.title}</h2>
          <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-foreground-500">{current.description}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/channel/${current.id}`)}
              className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-fill"></i>
              </span>
              进入直播间
            </button>
            <button
              type="button"
              onClick={() => navigate(`/category/${current.categoryId}`)}
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
            >
              查看更多同类直播
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(active - 1)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/70 text-foreground-700 backdrop-blur transition-colors hover:text-primary-500 md:flex"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(active + 1)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/70 text-foreground-700 backdrop-blur transition-colors hover:text-primary-500 md:flex"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-4 right-6 hidden items-center gap-2 md:flex">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all ${index === active ? "w-6 bg-primary-500" : "w-1.5 bg-background-600"}`}
            aria-label={`切换到第 ${index + 1} 个直播`}
          />
        ))}
      </div>
    </div>
  );
}